import { useState } from 'react';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { FormLabel } from '@components/form-label';
import { RadioGroup } from '@components/radio-group';
import { Stack } from '@components/stack';
import { Text } from '@components/text';

import { useExportApplicants } from 'api/jobs/hooks';
import { useAuthenticatedUser } from 'features/authentication';
import { Modal } from 'features/modals';
import { useStore } from 'models/Provider';

export const ExportJobModal = observer(({ job, closeModal }) => {
  const [submitted, setSubmitted] = useState(false);
  const { t, current } = useStore();
  const [exportType, setExportType] = useState('xlsx');
  const me = useAuthenticatedUser();
  const exportApplicants = useExportApplicants();
  const { useSsnRegistry } = current.info;

  let availableFields = {
    id: { label: t('exportmodal.fields.id'), checked: false },
    name: { label: t('exportmodal.fields.name'), checked: false },
    email: { label: t('exportmodal.fields.email'), checked: false },
    phone: { label: t('exportmodal.fields.phone'), checked: false },
    ...(useSsnRegistry && { ssn: { label: t('exportmodal.fields.ssn'), checked: false } }),
    ...(useSsnRegistry && { age: { label: t('exportmodal.fields.age'), checked: false } }),
    ...(useSsnRegistry && { gender: { label: t('exportmodal.fields.gender'), checked: false } }),
    date: { label: t('exportmodal.fields.date'), checked: false },
    is_rejected: { label: t('exportmodal.fields.is_rejected'), checked: false },
    is_notified_of_rejection: {
      label: t('exportmodal.fields.is_notified_of_rejection'),
      checked: false,
    },
    date_of_rejection: { label: t('exportmodal.fields.date_rejected'), checked: false },
    is_hired: { label: t('exportmodal.fields.is_hired'), checked: false },
    date_of_hire: { label: t('exportmodal.fields.date_hired'), checked: false },
    is_retracted: { label: t('exportmodal.fields.is_retracted'), checked: false },
    is_manually_added: { label: t('exportmodal.fields.is_manually_added'), checked: false },
    referred_site: { label: t('exportmodal.fields.referred_site'), checked: false },
    job_stage_name: { label: t('exportmodal.fields.stage_name'), checked: false },
    num_reviews: { label: t('exportmodal.fields.num_reviews'), checked: false },
    num_notes: { label: t('exportmodal.fields.num_notes'), checked: false },
  };
  const [fields, setFields] = useState(availableFields);
  const fieldsArray = Object.entries(fields).map((e) => ({ id: e[0], ...e[1] }));
  function addOrRemoveField(fieldId) {
    let newFields = Object.assign({}, fields);
    newFields[fieldId].checked = !newFields[fieldId].checked;
    setFields(newFields);
  }

  let fieldList = [];
  for (let key in fields) {
    const field = fields[key];
    fieldList.push(
      <div>
        <Checkbox
          key={key}
          label={field.label}
          value={field.checked}
          onChange={() => addOrRemoveField(key)}
        />
      </div>,
    );
  }

  const selectedAllFields = fieldsArray.every((field) => field.checked);
  const selectAllFields = () => {
    let newFields = Object.assign({}, fields);
    for (let key in newFields) newFields[key].checked = true;
    setFields(newFields);
  };
  const unselectAllFields = () => {
    let newFields = Object.assign({}, fields);
    for (let key in newFields) newFields[key].checked = false;
    setFields(newFields);
  };

  let availableQuestions = {};
  let defaultLanguage =
    job.languages.find(
      (lang) => lang.isEnabled && lang.language === current.info.defaultLanguage,
    ) ?? null;

  if (!defaultLanguage && job.languages.length > 0) {
    defaultLanguage = job.languages.find((lang) => lang.isEnabled) ?? job.languages[0];
  }

  defaultLanguage.form.sections.map((s) =>
    s.questions.forEach((q) => {
      if (q.type !== 'attachment') availableQuestions[q.id] = { label: q.title, checked: false };
    }),
  );
  const [questions, setQuestions] = useState(availableQuestions);
  const questionsArray = Object.entries(questions).map((e) => ({ id: e[0], ...e[1] }));

  function addOrRemoveQuestion(questionId) {
    let newQuestions = Object.assign({}, questions);
    newQuestions[questionId].checked = !newQuestions[questionId].checked;
    setQuestions(newQuestions);
  }
  let questionList = [];
  for (let key in questions) {
    const question = questions[key];
    questionList.push(
      <div>
        <Checkbox
          key={key}
          label={question.label}
          checked={question.checked}
          onChange={() => addOrRemoveQuestion(key)}
        />
      </div>,
    );
  }

  const selectedAllQuestions = questionsArray.every((question) => question.checked);
  const selectAllQuestions = () => {
    let newQuestions = Object.assign({}, questions);
    for (let key in newQuestions) newQuestions[key].checked = true;
    setQuestions(newQuestions);
  };
  const unselectAllQuestions = () => {
    let newQuestions = Object.assign({}, questions);
    for (let key in newQuestions) newQuestions[key].checked = false;
    setQuestions(newQuestions);
  };

  function onSubmit() {
    let checkedFields = [];
    for (let key in fields) {
      if (fields[key].checked) checkedFields.push(key);
    }
    for (let key in questions) {
      if (questions[key].checked) checkedFields.push(key);
    }
    const success = exportApplicants.mutateAsync({
      jobId: job.id,
      fields: checkedFields,
      exportType: exportType,
    });
    if (success) {
      setSubmitted(true);
    }
  }

  if (submitted) {
    return (
      <Modal>
        <Modal.Header text={t('exportmodal.submitted.title')} />

        <Text
          text={
            <>
              {t('exportmodal.submitted.confirmtext.sent')} <strong>{me.email}</strong>{' '}
              {t('exportmodal.submitted.confirmtext.ready')}
            </>
          }
        />

        <Text text={t('exportmodal.submitted.notice')} />
        <Modal.Actions>
          <Button onClick={closeModal} text={t('exportmodal.button.close')} />
        </Modal.Actions>
      </Modal>
    );
  }
  return (
    <Modal>
      <Modal.Header text={t('exportmodal.title')} />
      <div>
        <RadioGroup
          defaultValue={exportType}
          label={t('exportmodal.labels.exporttype')}
          onChange={setExportType}
        >
          <RadioGroup.Option label={t('exportmodal.exporttype.xlsx')} value="xlsx" />
          <RadioGroup.Option label={t('exportmodal.exporttype.csv')} value="csv" />
        </RadioGroup>
      </div>
      <Stack space={4}>
        <Stack space={2}>
          <Stack space={0} orientation="horizontal" justify="space-between">
            <FormLabel text={t('exportmodal.labels.headers')} />
            <SelectAll onClick={selectedAllFields ? unselectAllFields : selectAllFields}>
              {selectedAllFields ? t('exportmodal.deselect') : t('exportmodal.select')}
            </SelectAll>
          </Stack>
          <div>{fieldList.map((field) => field)}</div>
        </Stack>
        <Stack space={2}>
          <Stack space={0} orientation="horizontal" justify="space-between">
            <FormLabel text={t('exportmodal.labels.questions')} />
            <SelectAll onClick={selectedAllQuestions ? unselectAllQuestions : selectAllQuestions}>
              {selectedAllQuestions ? t('exportmodal.deselect') : t('exportmodal.select')}
            </SelectAll>
          </Stack>
          <div>{questionList.map((question) => question)}</div>
        </Stack>
      </Stack>

      <Modal.Actions>
        <Button variant="secondary" onClick={closeModal} text={t('exportmodal.button.cancel')} />
        <Button onClick={onSubmit} text={t('exportmodal.button.submit')} />
      </Modal.Actions>
    </Modal>
  );
});

const SelectAll = styled.div((p) => ({
  ':hover': {
    cursor: 'pointer',
  },
  ...p.theme.text.regular14,
  opacity: 0.8,
  color: p.theme.colors.purple40,
  fontStyle: 'italic',
}));
